/* import __COLOCATED_TEMPLATE__ from './audience-filter.hbs'; */
/* RESPONSIBLE TEAM: team-data-foundations */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import type AiContentSegmentsService from 'embercom/services/ai-content-segments-service';
import type AiContentSegment from 'embercom/models/ai-content-segment';
import { tracked } from '@glimmer/tracking';
import { scheduleOnce } from '@ember/runloop';
import { helper } from '@ember/component/helper';
import safeWindowOpen from 'embercom/lib/safe-window-open';
import type RouterService from '@ember/routing/router-service';
import type AppService from 'embercom/services/app-service';
import {
  AUDIENCE_FILTER_EVERYONE,
  AUDIENCE_FILTER_CUSTOM,
  AUDIENCE_FILTER_NA,
} from './audience-filter-constants';

interface Signature {
  Args: {
    onSelectionChange: (aiContentSegmentIds?: string[]) => void;
    selectedValues?: string[];
  };
  Blocks: {
    default?: never;
  };
  Element: HTMLDivElement;
}

export default class WorkflowConnectorAudienceFilter extends Component<Signature> {
  @service declare aiContentSegmentsService: AiContentSegmentsService;
  @service declare intl: any;
  @service declare router: RouterService;
  @service declare appService: AppService;

  @tracked selectedFilterValues: string[] = [];

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    this.selectedFilterValues = [...(args.selectedValues || [])];
    if (!this.aiContentSegmentsService.isLoading) {
      this.aiContentSegmentsService.loadSegments().catch((error) => {
        console.error('Failed to load audience segments:', error);
      });
    }
  }

  get segments() {
    return this.aiContentSegmentsService.segments;
  }

  get dropdownLabel() {
    if (this.selectedFilterValues.length === 0) {
      return this.intl.t('workflow-connector.actions.filters.audience.label');
    }

    let labels: string[] = [];
    let selectedSegmentIds = this.selectedFilterValues.filter(
      (id) =>
        id !== AUDIENCE_FILTER_EVERYONE &&
        id !== AUDIENCE_FILTER_CUSTOM &&
        id !== AUDIENCE_FILTER_NA,
    );

    if (this.selectedFilterValues.includes(AUDIENCE_FILTER_EVERYONE)) {
      labels.push(this.intl.t('workflow-connector.actions.filters.audience.everyone'));
    }
    if (this.selectedFilterValues.includes(AUDIENCE_FILTER_CUSTOM)) {
      labels.push(this.intl.t('common.audience-selector-dropdown.custom'));
    }
    if (this.selectedFilterValues.includes(AUDIENCE_FILTER_NA)) {
      labels.push(this.intl.t('workflow-connector.actions.audience.not-applicable'));
    }

    if (selectedSegmentIds.length > 0 && this.segments) {
      let selectedSegments = this.segments.filter((segment) =>
        selectedSegmentIds.includes(segment.id),
      );

      if (selectedSegments.length === 1) {
        labels.push(selectedSegments[0].nameWithFallback);
      } else if (selectedSegments.length > 1) {
        labels.push(
          `${selectedSegments[0].nameWithFallback} and ${selectedSegments.length - 1} more`,
        );
      }
    }

    return labels.join(', ');
  }

  isFilterSelected = helper(([filterId, selectedValues]: [string, string[]]): boolean => {
    return selectedValues.includes(filterId);
  });

  get filterOptions() {
    return [
      {
        id: AUDIENCE_FILTER_EVERYONE,
        label: this.intl.t('workflow-connector.actions.filters.audience.everyone'),
        action: () => this.toggleFilterValue(AUDIENCE_FILTER_EVERYONE),
        testId: 'everyone',
      },
      {
        id: AUDIENCE_FILTER_CUSTOM,
        label: this.intl.t('common.audience-selector-dropdown.custom'),
        action: () => this.toggleFilterValue(AUDIENCE_FILTER_CUSTOM),
        testId: 'custom',
      },
      {
        id: AUDIENCE_FILTER_NA,
        label: this.intl.t('workflow-connector.actions.audience.not-applicable'),
        action: () => this.toggleFilterValue(AUDIENCE_FILTER_NA),
        testId: 'na',
      },
    ];
  }

  @action
  syncWithProps() {
    let propsValues = this.args.selectedValues || [];
    let propsSorted = [...propsValues].sort();
    let currentSorted = [...this.selectedFilterValues].sort();

    if (JSON.stringify(propsSorted) !== JSON.stringify(currentSorted)) {
      this.selectedFilterValues = [...propsValues];
    }
  }

  @action
  toggleSegmentFilter(segment: AiContentSegment) {
    this.toggleFilterValue(segment.id);
  }

  private toggleFilterValue(filterId: string) {
    let index = this.selectedFilterValues.indexOf(filterId);
    let newSelection = [...this.selectedFilterValues];

    if (index >= 0) {
      newSelection.splice(index, 1);
    } else {
      newSelection.push(filterId);
    }

    this.selectedFilterValues = newSelection;
    scheduleOnce('afterRender', this, this.notifySelectionChange);
  }

  private notifySelectionChange() {
    this.args.onSelectionChange(this.selectedFilterValues);
  }

  @action
  redirectToAudienceSettings() {
    let route = this.appService.app.canUseMultiBrandGA
      ? 'apps.app.settings.ai-automation.audiences'
      : 'apps.app.settings.audiences';
    let url = this.router.urlFor(route, this.appService.app.id);
    safeWindowOpen(url, '_blank');
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'WorkflowConnector::AudienceFilter': typeof WorkflowConnectorAudienceFilter;
    'workflow-connector/audience-filter': typeof WorkflowConnectorAudienceFilter;
  }
}
